import React, { useState, useEffect, useContext } from 'react';

import { Typography, Button, Box, LinearProgress, useTheme } from '@mui/material';

import axiosApiInstance from '@/api/shared/axios-instance';
import CustomSectionWrapper from '@/DesignSystem/wrappers/CustomSectionWrapper';
import { Context } from '@/context';
import { esFechaMayorOIgualActual } from '@/helpers/datetime-helper';
import URL_FACTORY from '@/helpers/url-helper';

const VoteOption = ({ option, label, votes, totalVotes, selectedOption, onVote, isLoaded, isDisabled }) => {
    const getPercentage = (votes) => {
        if (!totalVotes || totalVotes === 0 || votes === 0) return 0;
        return (votes / totalVotes) * 100;
    };

    const percentage = getPercentage(votes);

    return (
        <Box sx={{ width: '32%' }}>
            <Button
                variant="outlined"
                onClick={() => onVote(option)}
                disabled={isDisabled}
                sx={{
                    color: 'white',
                    fontSize: 14,
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    opacity: selectedOption && selectedOption !== option ? 0.5 : 1,
                    borderColor: 'border.dark',
                }}
            >
                <Typography component="span" fontSize={12} sx={{ lineHeight: 1 }} noWrap>
                    {label}
                </Typography>
            </Button>
            {isLoaded && (
                <>
                    <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{ height: 10, borderRadius: 5, mt: 1 }}
                    />
                    <Typography variant="body2" sx={{ textAlign: 'center' }} fontSize={12}>
                        {percentage.toFixed(2)}%
                    </Typography>
                </>
            )}
        </Box>
    );
};

const VoteFixtureComponent = ({ data }) => {
    const theme = useTheme();
    const fixtureId = data?.fixture?.id;
    const homeName = data?.fixture?.home?.name;
    const awayName = data?.fixture?.away?.name;
    const fixtureDate = data?.fixture?.date;

    const [votes, setVotes] = useState({ 1: 0, X: 0, 2: 0 });
    const [totalVotes, setTotalVotes] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const partidoComenzado = esFechaMayorOIgualActual(fixtureDate);

    const { state } = useContext(Context);
    const { user } = state;

    const getTotalVotes = () => {
        if (user.logged && totalVotes > 50) {
            return ` (${totalVotes} votos)`;
        }
        return '';
    };

    const fetchVotes = (fixtureId) => {
        axiosApiInstance.post(URL_FACTORY.GetVotes, { fixtureId }).then((response) => {
            if (response?.data && response.data?.totalVotes > 0) {
                const options = response.data.options.reduce((acc, option) => {
                    acc[option.key] = option.votes;
                    return acc;
                }, {});
                setVotes(options);
                setTotalVotes(response.data.totalVotes);
                setSelectedOption(response.data.userVote);
                setIsLoaded(true);
            }
        });
    };

    useEffect(() => {
        if (fixtureId > 0 && partidoComenzado) {
            fetchVotes(fixtureId);
        } else {
            setIsLoaded(false);
            setSelectedOption(null);
        }
    }, [fixtureId]);

    const handleVote = (option) => {
        if (isLoaded) return;

        axiosApiInstance
            .post(URL_FACTORY.AddVote, {
                fixtureId,
                key: option,
            })
            .then((response) => {
                if (response?.data) {
                    const options = response.data.options.reduce((acc, option) => {
                        acc[option.key] = option.votes;
                        return acc;
                    }, {});
                    setVotes(options);
                    setTotalVotes(response.data.totalVotes);
                    setSelectedOption(option);
                    setIsLoaded(true);
                }
            })
            .catch(() => {});
    };

    const voteOptions = [
        { key: '1', label: homeName ?? '1' },
        { key: 'X', label: 'Empate' },
        { key: '2', label: awayName ?? '2' },
    ];

    return (
        <CustomSectionWrapper
            title={`¿Quién crees que ganará?${getTotalVotes()}`}
            color={theme.palette.primary.main}
            mb={2}
        >
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.5, width: '100%', gap: '2%' }}>
                {voteOptions.map(({ key, label }) => (
                    <VoteOption
                        key={key}
                        option={key}
                        label={label}
                        votes={votes[key] || 0}
                        totalVotes={totalVotes}
                        selectedOption={selectedOption}
                        onVote={handleVote}
                        isLoaded={isLoaded}
                        isDisabled={(selectedOption && selectedOption !== key) || partidoComenzado}
                    />
                ))}
            </Box>
        </CustomSectionWrapper>
    );
};

export default VoteFixtureComponent;
